/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-10-10
 */

import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';

interface LoginMessage {
    username: string;
    password: string;
}

export interface UserDetail {
    id: string,
    userName: string,
    firstName: string,
    lastName: string,
    email: string,
    cellphone: string,
    isAdmin: boolean;
    roles: string[];  
    userData: {
        stores: string[];  
    };
    admin: boolean;
}

// Define the shape of the AuthContext
interface AuthContextType {
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
    fetchData: (endpoint: string, options: RequestInit, processData?: (result: any, status: string) => any, clean?: () => void) => void;
    fetchDataMulti: (endpoints: string[], options: RequestInit, processData?: (result?: any) => any, clean?: () => void) => void; 
    userDetail: UserDetail | null;
    token: string | null;
    responseErrorMessage: string | null;
    responseStatus: string | null;
    showErrorAlert: boolean; 
    handleCloseErrorAlert: () => void; 
    versionControl: VersionControlType | null;
}

interface VersionControlType {
  pomVersion: string,
  buildCounter: number,
  buildDate: string
}

// interface RequestInit {
//     method?: string; // HTTP method like 'GET', 'POST', etc.
//     headers?: HeadersInit; // Headers or an object with key-value pairs
//     body?: BodyInit | null; // The request body, usually for POST or PUT requests
//     mode?: RequestMode; // CORS settings: 'cors', 'no-cors', 'same-origin'
//     credentials?: RequestCredentials; // Include credentials: 'include', 'same-origin', 'omit'
//     cache?: RequestCache; // Caching behavior: 'default', 'no-cache', 'reload', 'force-cache', etc.
//     redirect?: RequestRedirect; // Redirect behavior: 'follow', 'manual', 'error'
//     referrer?: string; // Referrer URL
//     referrerPolicy?: ReferrerPolicy; // Referrer policy like 'no-referrer', 'origin', etc.
//     integrity?: string; // Subresource integrity value
//     keepalive?: boolean; // Keeps the connection alive
//     signal?: AbortSignal | null; // Abort signal to cancel the request
//     window?: any; // Used for some old, non-standardized fetch API options
// }
  

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [token, setToken] = useState<string | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [credentials, setCredentials] = useState<string>("");
    const [versionControl ,setVersionControl] = useState<VersionControlType | null>(null);
    const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
    const [responseErrorMessage, setResponseErrorMessage] = useState<string | null>(null);
    const [responseStatus, setResponseStatus]  = useState<string | null>(null);
    const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);   

    useEffect(() => {
        
        try {
            const loggedInUrl: string = process.env.REACT_APP_LOGGED_IN_URL as string;
        
            fetchData(loggedInUrl, {method:"GET"}, (result: any) => {
                console.log(`User Information`, result);
                setUserDetail(result);
            });
            
        } catch (error) {
            
        }
       
        //checking version
        const versionUrl: string = process.env.REACT_APP_VERSION_URL as string;
        fetchData(versionUrl, {method:"GET"}, (result: any) => {
            setVersionControl(result);
        });
          
    }, []);

    const handleCloseErrorAlert = () => {
        setShowErrorAlert(false);
    }

    // Login
    const login = async (username: string, password: string) => {
        
        const loginUrl: string = process.env.REACT_APP_LOGIN_URL as string;

        setCredentials(btoa(`${username}:${password}`));
        
        const loginMessage: LoginMessage ={
            username: username,
            password: password,
        }

        fetchData(loginUrl, {method:"POST", body: JSON.stringify(loginMessage)}, (result: any) => {
            const token = result;
            setToken(token);
            localStorage.setItem("token", token);
            setIsAuthenticated(true);

            const loggedInUrl: string = process.env.REACT_APP_LOGGED_IN_URL as string;
            fetchData(loggedInUrl, {method:"GET"}, (result: any) => {
                console.log(`User Information`, result);
                setUserDetail(result);
                setIsAuthenticated(true);
            });

        });

    };

    // Logout
    const logout = () => {
        setToken(null);
        setIsAuthenticated(false);
        setUserDetail(null);
        setCredentials("");
        localStorage.removeItem("token");
    };

    // Fetch Data from a Single Endpoint
    const fetchData = async (   endpoint: string ,
                                options: RequestInit = {},
                                processData?: (result?: any, status?: any) => any,
                                clean?: () => void
                            ) => {
        
        const headers = new Headers(options.headers);
        if(! headers.has('Content-Type')) {
              headers.append('Content-Type', 'application/json');
        }else{
            if(headers.get("Content-Type") === "multipart/formdata"){
               headers.delete("Content-Type");
            } 
        }
        options.headers = headers;
        console.log('Using Content-Type : ');
        console.log(options.headers.get('Content-Type'));

        if(credentials.length > 0){
            options.headers.append("Authorization", `Basic ${credentials}`)
        } else {
            options.headers.append("Authorization", `Bearer ${localStorage.getItem("token")}`)
        }

        try {
            const response = await fetch(endpoint, options);

            if(response.status === 403) {
                console.log("fectchData->logout()");
                logout();
            }
            setResponseErrorMessage(null);
            setResponseStatus(null);
            setShowErrorAlert(false);
            if (response.ok) {
                setIsAuthenticated(true);

                // console.log("response (OK):", response.status);
                
                const contentType = response.headers.get("Content-Type");
                let result: any = null; 

                if(contentType?.includes("application/json")){
            
                    result = await response.json();
                } else if (contentType?.includes("text/html") || contentType?.includes("text/plain")){
  
                    result = await response.text();
                } else {

                    result = await response.blob();
                }
                 
                if (processData) {
                    processData(result, response.status.toString());
                    console.log("fetchData->processData()");
                    setResponseErrorMessage(null); //resetting error response  message to null
                    setShowErrorAlert(false);
                    setResponseStatus(response.status.toString());
                } else {
                    console.log("fetchData");
                }
            } else {
                // console.log("response (NOT OKAY):", response.status);
                setResponseStatus(response.status.toString());
                const errorBody = await response.json(); 
                setResponseErrorMessage(`Response Status: ${response?.status} , Response Message: ${JSON.stringify(errorBody)}`); 
                setShowErrorAlert(true); //setting show error to true cause of error 
            }

        } catch (error) {
            console.error(error);
            setResponseErrorMessage(JSON.stringify(error)); 
            setShowErrorAlert(true);
        } finally {
            if(clean){
                clean();
            }
        }
    }   
 
    // Fetch Data from Multiple Endpoints
    const fetchDataMulti = async (  endpoints: string[],
                                    options: RequestInit = {},
                                    processData?: (result?: any) => any,
                                    clean?: () => void
                            ) => {
        
        const headers = new Headers(options.headers);
        if(! headers.has('Content-Type')) {
              headers.append('Content-Type', 'application/json');
        }else{
            if(headers.get("Content-Type") === "multipart/formdata"){
               headers.delete("Content-Type");
            } 
        }
        options.headers = headers;
        console.log('Using Content-Type : ');
        console.log(options.headers.get('Content-Type'));

        if(credentials.length > 0){
            options.headers.append("Authorization", `Basic ${credentials}`)
        } else {
            options.headers.append("Authorization", `Bearer ${localStorage.getItem("token")}`)
        }

        const loadedPromises: any[] = [];

        for(let ep in endpoints) {
            loadedPromises.push(
                fetch(endpoints[ep], options)
            );
        }

        try {
            const result = (
                await Promise.all(loadedPromises)
            ).map((response) => {
                const contentType = response.headers.get("Content-Type"); 
                if(contentType?.includes("application/json")){
                    return response.json();
                } else if (contentType?.includes("text/html") || contentType?.includes("text/plain")){
                    return response.text();
                } else {
                    return response.blob();
                }
            });

            const results: any[] = await Promise.all(result);

            if (processData) {
                processData(results);
                console.log("fetchData->processData()");
                setResponseErrorMessage(null); //resetting error response  message to null
                setShowErrorAlert(false);
            } else {
                console.log("fetchData");
            }
        } catch (error) {
            console.error(error);
            setResponseErrorMessage(JSON.stringify(error)); 
            setShowErrorAlert(true);
        } finally {
            if(clean){
                clean();
            }
        }
    } 

    // Provide authentication context to the app
    return (
        <AuthContext.Provider value={{ 
            isAuthenticated, 
            login, 
            logout, 
            fetchData,
            fetchDataMulti,
            userDetail, 
            token, 
            responseErrorMessage,
            showErrorAlert,
            handleCloseErrorAlert,
            versionControl,
            responseStatus
            }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
